import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Plus, Search, Download, Trash2, Edit, Filter,
  ChevronLeft, ChevronRight, Calendar, CheckCircle2, Loader2
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const VaccinationsPage = () => {
  const navigate = useNavigate();
  const [vaccinations, setVaccinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [deleteId, setDeleteId] = useState(null);
  const [boletaEdits, setBoletaEdits] = useState({});
  const [savingBoletaId, setSavingBoletaId] = useState(null);
  const limit = 20;

  const formatFecha = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return (y && m && d) ? `${d}/${m}/${y}` : iso;
  };

  const handleSaveBoleta = async (vacc) => {
    const valor = boletaEdits[vacc.id];
    if (valor === undefined || valor.trim() === (vacc.numero_boleta || '')) return;
    setSavingBoletaId(vacc.id);
    try {
      await axios.put(`${API}/vaccinations/${vacc.id}/boleta`,
        { numero_boleta: valor.trim() },
        { withCredentials: true }
      );
      toast.success('Nº de boleta guardado');
      setBoletaEdits(prev => {
        const copy = { ...prev };
        delete copy[vacc.id];
        return copy;
      });
      setVaccinations(prev => prev.map(v => v.id === vacc.id ? { ...v, numero_boleta: valor.trim() } : v));
    } catch (error) {
      console.error('Error saving boleta:', error);
      toast.error('Error al guardar el Nº de boleta');
    } finally {
      setSavingBoletaId(null);
    }
  };

  const [filters, setFilters] = useState({
    fechaInicio: '',
    fechaFin: '',
    mes: '',
    anio: new Date().getFullYear().toString(),
    vacuna: '',
    search: ''
  });

  const months = [
    { value: '1', label: 'Enero' },
    { value: '2', label: 'Febrero' },
    { value: '3', label: 'Marzo' },
    { value: '4', label: 'Abril' },
    { value: '5', label: 'Mayo' },
    { value: '6', label: 'Junio' },
    { value: '7', label: 'Julio' },
    { value: '8', label: 'Agosto' },
    { value: '9', label: 'Septiembre' },
    { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' },
    { value: '12', label: 'Diciembre' },
  ];

  const fetchVaccinations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('skip', page * limit);
      
      if (filters.fechaInicio && filters.fechaFin) {
        params.append('fecha_inicio', filters.fechaInicio);
        params.append('fecha_fin', filters.fechaFin);
      } else if (filters.mes && filters.anio) {
        params.append('mes', filters.mes);
        params.append('anio', filters.anio);
      }
      
      if (filters.vacuna) {
        params.append('vacuna', filters.vacuna);
      }

      const response = await axios.get(`${API}/vaccinations?${params.toString()}`, {
        withCredentials: true
      });
      setVaccinations(response.data);

      // Get count
      const countParams = new URLSearchParams();
      if (filters.fechaInicio && filters.fechaFin) {
        countParams.append('fecha_inicio', filters.fechaInicio);
        countParams.append('fecha_fin', filters.fechaFin);
      }
      const countResponse = await axios.get(`${API}/vaccinations/count?${countParams.toString()}`, {
        withCredentials: true
      });
      setTotalCount(countResponse.data.count);
    } catch (error) {
      console.error('Error fetching vaccinations:', error);
      toast.error('Error al cargar las vacunaciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVaccinations();
    // Refetch when page/month/year changes. fetchVaccinations closes over filters
    // (search/empresa) which should NOT auto-refetch—those use handleSearch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters.mes, filters.anio]);

  const handleSearch = () => {
    setPage(0);
    fetchVaccinations();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await axios.delete(`${API}/vaccinations/${deleteId}`, {
        withCredentials: true
      });
      toast.success('Vacunación eliminada');
      fetchVaccinations();
    } catch (error) {
      toast.error('Error al eliminar');
    } finally {
      setDeleteId(null);
    }
  };

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.fechaInicio && filters.fechaFin) {
        params.append('fecha_inicio', filters.fechaInicio);
        params.append('fecha_fin', filters.fechaFin);
      } else if (filters.mes && filters.anio) {
        params.append('mes', filters.mes);
        params.append('anio', filters.anio);
      }

      const response = await axios.get(`${API}/export/vaccinations/excel?${params.toString()}`, {
        withCredentials: true,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `vacunaciones_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Excel descargado');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="vaccinations-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 heading">Vacunaciones</h1>
            <p className="text-slate-500 mt-1">
              {totalCount} registros encontrados
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={handleExportExcel}
              className="gap-2"
              data-testid="export-excel-btn"
            >
              <Download className="w-4 h-4" />
              Exportar Excel
            </Button>
            <Link to="/vaccinations/new">
              <Button className="btn-accent gap-2" data-testid="new-vaccination-btn">
                <Plus className="w-4 h-4" />
                Nueva Vacunación
              </Button>
            </Link>
          </div>
        </div>

        {/* Filters */}
        <Card data-testid="filters-card">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 block">
                  Mes
                </label>
                <Select 
                  value={filters.mes} 
                  onValueChange={(value) => setFilters({...filters, mes: value === "all" ? "" : value})}
                >
                  <SelectTrigger data-testid="filter-month">
                    <SelectValue placeholder="Seleccionar mes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {months.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 block">
                  Año
                </label>
                <Select 
                  value={filters.anio} 
                  onValueChange={(value) => setFilters({...filters, anio: value})}
                >
                  <SelectTrigger data-testid="filter-year">
                    <SelectValue placeholder="Año" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 16 }, (_, i) => 2020 + i).reverse().map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 block">
                  Fecha Inicio
                </label>
                <Input
                  type="date"
                  value={filters.fechaInicio}
                  onChange={(e) => setFilters({...filters, fechaInicio: e.target.value})}
                  className="input-clinical"
                  data-testid="filter-date-start"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 block">
                  Fecha Fin
                </label>
                <Input
                  type="date"
                  value={filters.fechaFin}
                  onChange={(e) => setFilters({...filters, fechaFin: e.target.value})}
                  className="input-clinical"
                  data-testid="filter-date-end"
                />
              </div>

              <div className="flex items-end">
                <Button 
                  onClick={handleSearch}
                  className="w-full gap-2"
                  data-testid="search-btn"
                >
                  <Search className="w-4 h-4" />
                  Buscar
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <div className="table-wrapper overflow-x-auto" data-testid="vaccinations-table">
          <table className="w-full min-w-[1000px]">
            <thead className="table-header">
              <tr>
                <th className="p-4">Fecha</th>
                <th className="p-4">Paciente</th>
                <th className="p-4">Documento</th>
                <th className="p-4">Edad</th>
                <th className="p-4">Vacuna</th>
                <th className="p-4">Dosis</th>
                <th className="p-4">Monto</th>
                <th className="p-4">Tipo Pago</th>
                <th className="p-4">Boleta</th>
                <th className="p-4 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} className="p-8 text-center">
                    <div className="loading-spinner w-8 h-8 mx-auto"></div>
                  </td>
                </tr>
              ) : vaccinations.length === 0 ? (
                <tr>
                  <td colSpan={10} className="p-8 text-center text-slate-500">
                    <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay vacunaciones registradas</p>
                  </td>
                </tr>
              ) : (
                vaccinations.map((vacc) => (
                  <tr key={vacc.id} className="table-row" data-testid={`vaccination-row-${vacc.id}`}>
                    <td className="table-cell font-mono text-sm">
                      {formatFecha(vacc.fecha_vacunacion)}
                    </td>
                    <td className="table-cell font-medium">
                      {vacc.nombre_completo}
                    </td>
                    <td className="table-cell font-mono text-sm">
                      {vacc.documento_consolidado}
                    </td>
                    <td className="table-cell">
                      <span className="badge-neutral">{vacc.edad}</span>
                    </td>
                    <td className="table-cell">{vacc.vacuna}</td>
                    <td className="table-cell">{vacc.dosis}</td>
                    <td className="table-cell font-mono">
                      ${vacc.monto?.toLocaleString('es-CL') || 0}
                    </td>
                    <td className="table-cell">
                      <span className="badge-success">{vacc.tipo_pago}</span>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-1">
                        <Input
                          value={boletaEdits[vacc.id] !== undefined ? boletaEdits[vacc.id] : (vacc.numero_boleta || '')}
                          onChange={(e) => setBoletaEdits(prev => ({ ...prev, [vacc.id]: e.target.value }))}
                          onKeyDown={(e) => e.key === 'Enter' && handleSaveBoleta(vacc)}
                          placeholder="Nº Boleta"
                          className="h-8 w-28 text-sm font-mono"
                          data-testid={`input-boleta-${vacc.id}`}
                        />
                        {boletaEdits[vacc.id] !== undefined && boletaEdits[vacc.id].trim() !== (vacc.numero_boleta || '') && (
                          <Button
                            size="sm"
                            className="h-8 px-2 bg-emerald-600 hover:bg-emerald-700"
                            disabled={savingBoletaId === vacc.id}
                            onClick={() => handleSaveBoleta(vacc)}
                            title="Guardar Nº de boleta"
                            data-testid={`save-boleta-${vacc.id}`}
                          >
                            {savingBoletaId === vacc.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <CheckCircle2 className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </td>
                    <td className="table-cell text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/vaccinations/edit/${vacc.id}`)}
                          data-testid={`edit-btn-${vacc.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteId(vacc.id)}
                          data-testid={`delete-btn-${vacc.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Mostrando {page * limit + 1} - {Math.min((page + 1) * limit, totalCount)} de {totalCount}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                data-testid="next-page-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Delete Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar vacunación?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción no se puede deshacer. El registro será eliminado permanentemente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-btn"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default VaccinationsPage;
